import { AIEventTracker } from '@src/monitoring/utils/AIEventTracker';
import { logChatGptOutput } from '@src/utility/datadog';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { ServerEventStreamer } from '@src/utility/ServerEventStreamer';
import { getOpenAIKey } from '@src/utility/spaCredentials';
import OpenAI from 'openai';

import {
  type ResolvedModel,
  spreadModelParams,
} from '../repositories/resolveAIModel';

import type { AiFlowRetryCondition } from '../types.ts/ai_prompts';

const stepLogger = LogManager.getLogger('aiFlow:AIFlowStep');

type StreamPayload = any;
type StepResponse = string | StreamPayload[] | null;

export class AIFlowStep {
  id: string;
  label: string;
  systemMessageTemplate: string;
  userPromptTemplate: string;
  lastResult?: any;
  lastResponse?: StepResponse;
  allResponses: StepResponse[];
  systemMessage?: string;
  includeMessagesFromStepId?: string;
  inputMessages?: any[];
  userPrompt?: string;
  attempts: number = -1;
  maxAttempts: number;
  timeElapsed: number = 0;
  models: string[] = ['gpt-4o'];
  resolvedModels: ResolvedModel[] = [];
  config: {
    temperature: number;
    impactScoreKeys?: string[];
  } = {
    temperature: 0.5,
    impactScoreKeys: undefined,
  };
  status: 'pending' | 'finished' | 'running' | 'failed' = 'pending';
  retryCondition: boolean | AiFlowRetryCondition;
  mapper?: (flowContext: any, stepContext: AIFlowStep) => any;
  filter?: (flowContext: any, stepContext: AIFlowStep) => any;
  reducer?: (flowContext: any, stepContext: AIFlowStep) => any;
  logger?: (message: string, messageContext?: object, error?: Error) => void;
  onPartialResult?: (result: any) => void;
  stream: 'text' | 'object' | 'none';
  doneStreaming: boolean = false;
  iterateOver?: string;
  resultType: 'text' | 'timecodes' | 'indices' = 'text';
  loggingContext: Record<string, string | undefined> = {};

  /** Timestamp (Date.now()) when the first HTTP response/chunk arrived — set by makeCall/makeStreamCall/makeEventStreamCall */
  firstResponseAt?: number;

  context: any;

  openai = new OpenAI({
    apiKey: getOpenAIKey(),
    dangerouslyAllowBrowser: true,
    timeout: 60 * 1000,
    maxRetries: 3,
  });

  constructor(params: {
    id: string;
    label: string;
    systemMessageTemplate: string;
    userPromptTemplate: string;
    maxAttempts: number;
    models: string[];
    resolvedModels?: ResolvedModel[];
    config: {
      temperature: number;
      impactScoreKeys?: string[];
    };
    retryCondition: boolean | AiFlowRetryCondition;
    includeMessagesFromStepId?: string | null;
    mapper?: (flowContext: any, stepContext: AIFlowStep) => any;
    filter?: (flowContext: any, stepContext: AIFlowStep) => any;
    reducer?: (flowContext: any, stepContext: AIFlowStep) => any;
    iterateOver?: string;
    stream?: 'text' | 'object' | 'none';
    resultType: 'text' | 'timecodes' | 'indices';
  }) {
    this.id = params.id;
    this.label = params.label;
    this.systemMessageTemplate = params.systemMessageTemplate;
    this.userPromptTemplate = params.userPromptTemplate;
    this.allResponses = [];
    this.maxAttempts = params.maxAttempts;
    this.config = params.config;
    this.models = params.models;
    this.resolvedModels = params.resolvedModels || [];
    this.retryCondition = params.retryCondition;
    this.mapper = params.mapper;
    this.filter = params.filter;
    this.reducer = params.reducer;
    this.logger = console.log;
    this.iterateOver = params.iterateOver;
    this.stream = params.stream || 'none';
    this.doneStreaming = false;
    this.resultType = params.resultType;
  }

  clone() {
    const cloned = new AIFlowStep({
      id: this.id,
      label: this.label,
      systemMessageTemplate: this.systemMessageTemplate,
      userPromptTemplate: this.userPromptTemplate,
      maxAttempts: this.maxAttempts,
      models: this.models,
      resolvedModels: this.resolvedModels,
      config: { ...this.config },
      retryCondition: this.retryCondition,
      includeMessagesFromStepId: this.includeMessagesFromStepId,
      mapper: this.mapper,
      filter: this.filter,
      reducer: this.reducer,
      iterateOver: this.iterateOver,
      stream: this.stream,
      resultType: this.resultType,
    });
    cloned.loggingContext = { ...this.loggingContext };
    return cloned;
  }

  async init(context: any, inputMessages?: any[]) {
    this.log(`Initializing step ${this.label}`);
    this.context = context;
    this.inputMessages = inputMessages; //includeMessagesFromStepId
    this.attempts = -1;
    this.timeElapsed = 0;

    this.systemMessage = this.replaceVariablesInTemplate(
      this.systemMessageTemplate,
      context,
    );
    this.userPrompt = this.replaceVariablesInTemplate(
      this.userPromptTemplate,
      context,
    );
    this.lastResult = undefined;
    this.firstResponseAt = undefined;
  }

  replaceVariablesInTemplate(
    template: string,
    variablesMap: Record<string, unknown>,
    iterationElement?: unknown,
  ) {
    // Escape substituted values so they are a valid JS string-literal body in
    // every context they may land in (single-quote string, double-quote string,
    // backtick string, or the outer backtick-template text). Without this, a
    // value like `Dick's` breaks the surrounding JS literal and eval throws
    // `SyntaxError: Missing } in template expression`.
    const escape = (v: unknown) =>
      String(v ?? '')
        .replaceAll('\\', '\\\\')
        .replaceAll('`', '\\`')
        .replaceAll('${', '\\${')
        .replaceAll("'", "\\'")
        .replaceAll('"', '\\"')
        .replaceAll('\n', '\\n')
        .replaceAll('\r', '\\r');

    let result = template;
    if (iterationElement !== undefined) {
      result = result.replaceAll(
        '{{iterationElement}}',
        escape(iterationElement),
      );
    }
    for (const [key, value] of Object.entries(variablesMap)) {
      result = result.replaceAll(`{{${key}}}`, escape(value));
    }
    try {
      return eval('`' + result + '`');
    } catch (err) {
      stepLogger.warn('Prompt template eval failed', {
        step: this.label,
        error: extractErrorInfo(err),
      });
      throw err;
    }
  }

  async run() {
    this.log(`Running step ${this.label}`);
    this.status = 'running';
    let messages: any[] = this.inputMessages || [];
    if (messages.length === 0) {
      messages = messages.concat([
        { role: 'system', content: this.systemMessage },
      ]);
    }

    //todo iterate over
    //todo retry logic

    if (this.iterateOver) {
      if (
        this.context[this.iterateOver] &&
        this.context[this.iterateOver].length > 0
      ) {
        for (const item of this.context[this.iterateOver]) {
          const userPrompt = this.replaceVariablesInTemplate(
            this.userPromptTemplate,
            this.context,
            item,
          );
          messages = messages.concat([{ role: 'user', content: userPrompt }]);
          this.inputMessages = messages;
          await this.runStep(messages);
        }
      }
    } else {
      const userPrompt = this.replaceVariablesInTemplate(
        this.userPromptTemplate,
        this.context,
      );
      messages = messages.concat([{ role: 'user', content: userPrompt }]);
      this.inputMessages = messages;
      await this.runStep(messages);
    }
  }

  async runStep(messages: any[]) {
    this.log(`Step ${this.label}, prompts: ${JSON.stringify(messages)}`);
    const stepTracker = AIEventTracker.start('ai_flow_step', {
      step_label: this.label,
    });
    let retry = true;
    let response: typeof this.lastResponse = null;

    const handleResponse = (
      response: typeof this.lastResponse,
      done: boolean,
    ) => {
      this.lastResponse = response;
      this.doneStreaming = done;
      if (done) {
        logChatGptOutput({ 'stream-ai-clips': response });
      }
      if (this.mapper) {
        try {
          this.lastResult = this.mapper(this.context, this);
        } catch (error) {
          stepLogger.error('Error in mapper function', {
            step: this.label,
            error,
          });
          throw error;
        }
      }
      if (this.filter) {
        this.lastResult = this.filter(this.context, this);
      }
      if (this.reducer) {
        this.lastResult = this.reducer(this.context, this);
      }
      try {
        this.onPartialResult && this.onPartialResult(this.lastResult);
      } catch (error) {
        stepLogger.error('Error in handling partial result', {
          step: this.label,
          error,
        });
        throw error;
      }
    };

    while (retry) {
      this.attempts += 1;
      const attemptIndex = Math.min(this.attempts, this.models.length - 1);
      const currentResolved =
        this.resolvedModels.length > 0
          ? this.resolvedModels[
              Math.min(this.attempts, this.resolvedModels.length - 1)
            ]
          : undefined;
      const params = {
        temperature: this.config.temperature,
        model: this.models[attemptIndex],
        ...spreadModelParams(currentResolved),
        impact_score_keys: this.config.impactScoreKeys,
        result_type: this.resultType,
      };
      this.log(
        `Step ${this.label}, attempt ${this.attempts}, model: ${params.model}`,
      );
      retry = false;

      try {
        if (this.stream === 'text') {
          let streamResponse = '';
          await this.makeStreamCall(messages, params, (chunk, done) => {
            streamResponse += chunk;
            try {
              handleResponse(streamResponse, done);
            } catch (error) {
              stepLogger.error('Error in handling stream response', {
                step: this.label,
                error,
              });
              throw error;
            }
          });
          response = streamResponse || null;
        } else if (this.stream === 'object') {
          let streamResponseEvents: StreamPayload[] = [];
          await this.makeEventStreamCall(messages, params, payload => {
            stepLogger.debug('Got new event message', { step: this.label });
            streamResponseEvents.push(payload);
            try {
              handleResponse(streamResponseEvents, false);
            } catch (error) {
              stepLogger.error('Error in handling event message', {
                step: this.label,
                error,
              });
              throw error;
            }
          });
          handleResponse(streamResponseEvents, true);
          response = streamResponseEvents || null;
        } else {
          response = await this.makeCall(messages, params);
          handleResponse(response, true);
        }
        this.allResponses.push(response);
      } catch (err) {
        this.log(`Step ${this.label}, attempt ${this.attempts}, failed`);
        stepLogger.error(`Attempt ${this.attempts} failed`, {
          step: this.label,
          error: err,
        });
        retry = this.attempts < this.maxAttempts;
        continue; // next attempt
      }
      this.log(
        `Step ${this.label}, attempt ${
          this.attempts
        }, Streamed response: ${JSON.stringify(
          { response: this.lastResponse! },
          null,
          2,
        )}`,
      );
      if (
        !response &&
        typeof this.retryCondition === 'boolean' &&
        this.retryCondition === true
      ) {
        retry = this.attempts < this.maxAttempts;
      }
      if (typeof this.retryCondition === 'function') {
        retry = this.attempts < this.maxAttempts && this.retryCondition(this);
      }
    }
    this.log(
      `Step ${this.label}, attempt ${
        this.attempts
      }, transformed result: ${JSON.stringify(this.lastResult)}`,
    );
    const used = this.usedModel;
    const stepExtra = {
      provider: used?.provider,
      model_id: used?.model_id,
      attempt_number: this.attempts,
    };
    if (this.lastResult != null) {
      stepTracker.complete(stepExtra);
    } else {
      stepTracker.fail('No result produced', stepExtra);
    }
  }

  async makeCall(
    messages: any[],
    config: { temperature: number; model: string },
  ) {
    const completion = await this.openai.chat.completions.create({
      messages,
      ...config,
    });
    if (!this.firstResponseAt) this.firstResponseAt = Date.now();
    let response = completion.choices[0]?.message?.content;
    return response;
  }

  async makeEventStreamCall(
    messages: any[],
    config: { temperature: number; model: string },
    onMessageReceived: (message: any) => void,
  ) {
    await new ServerEventStreamer(
      `${process.env.REACT_APP_API_URL}/api/aiprompts/stream-ai-clips`,
      {
        messages,
        ...config,
        ...this.loggingContext,
      },
    ).start(
      message => {
        if (!this.firstResponseAt) this.firstResponseAt = Date.now();
        onMessageReceived(message);
      },
      error => {
        stepLogger.error('Error in event stream call', {
          step: this.label,
          error,
        });
        throw error;
      },
    );
  }

  async makeStreamCall(
    messages: any[],
    config: { temperature: number; model: string },
    onChunkReceived: (chunk: string, done: boolean) => void,
  ) {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/aiprompts/stream-ai-clips`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages,
          ...config,
          ...this.loggingContext,
        }),
      },
    );

    const data = res.body;
    if (!data) return null;
    const reader = data.getReader();
    const decoder = new TextDecoder();
    let isDone = false;

    while (!isDone) {
      const { value, done: doneReading } = await reader.read();
      isDone = doneReading;
      if (!this.firstResponseAt && !isDone) this.firstResponseAt = Date.now();
      let chunk = decoder.decode(value);
      onChunkReceived(chunk, isDone);
    }
  }

  /** Returns the model that was actually used during the last execution. */
  get usedModel(): { provider?: string; model_id: string } | undefined {
    if (this.attempts < 0) return undefined;
    if (this.resolvedModels.length > 0) {
      const idx = Math.min(this.attempts, this.resolvedModels.length - 1);
      return this.resolvedModels[idx];
    }
    if (this.models.length === 0) return undefined;
    const legacyIdx = Math.min(this.attempts, this.models.length - 1);
    return { provider: 'openai', model_id: this.models[legacyIdx] };
  }

  log(message: string) {
    this.logger && this.logger(message);
  }
}
